/**
 * What kind of Sales Navigator URL someone pasted into a "Find people by URL" source (PREC-4510).
 *
 * The form, the API DTO and the list run must agree on one answer: only a Sales Navigator people
 * search (`/sales/search/people…`) or a people list (`/sales/lists/people/…`) can be scraped for
 * people. Anything else is stored and sent to Unipile as a people search, which refuses it with a
 * bare 400 and left the user with an unexplained 500 preview and an "Error" list (company 1900,
 * list 7364). The classifier is pure so both apps can ask the same question, and the refusal names
 * the mistake rather than repeating "invalid URL".
 */

export type SalesNavigatorUrlKind =
	| "people_search"
	| "people_list"
	| "account_list"
	| "company_search"
	| "profile"
	| "invalid";

/** The kinds a Sales Navigator people source accepts. */
export const SALES_NAVIGATOR_PEOPLE_URL_KINDS: readonly SalesNavigatorUrlKind[] = [
	"people_search",
	"people_list",
];

const LINKEDIN_HOST = /^(?:[a-z0-9-]+\.)*linkedin\.com$/i;

function parseLinkedinUrl(value: string): URL | null {
	const trimmed = value.trim();

	if (!trimmed) {
		return null;
	}

	// A browser copy can arrive without the scheme; a bare host is still unambiguous.
	const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

	try {
		const url = new URL(candidate);

		return (url.protocol === "https:" || url.protocol === "http:") &&
			LINKEDIN_HOST.test(url.hostname)
			? url
			: null;
	} catch {
		return null;
	}
}

const pathStartsWith = (path: string, prefix: string): boolean =>
	path === prefix || path.startsWith(`${prefix}/`);

/**
 * The kind of URL `value` is. A URL that cannot be read at all is `invalid`; so is every other
 * LinkedIn page that is not a Sales Navigator people source.
 */
export function classifySalesNavigatorUrl(value: string): SalesNavigatorUrlKind {
	const url = parseLinkedinUrl(value);

	if (!url) {
		return "invalid";
	}

	const path = url.pathname.toLowerCase().replace(/\/+$/, "");

	if (pathStartsWith(path, "/sales/search/people")) {
		return "people_search";
	}

	if (path.startsWith("/sales/lists/people/") && path.length > "/sales/lists/people/".length) {
		return "people_list";
	}

	if (
		pathStartsWith(path, "/sales/accounts") ||
		pathStartsWith(path, "/sales/account") ||
		pathStartsWith(path, "/sales/lists/companies")
	) {
		return "account_list";
	}

	if (pathStartsWith(path, "/sales/search/company")) {
		return "company_search";
	}

	if (
		path === "/in" ||
		path.startsWith("/in/") ||
		path.startsWith("/pub/") ||
		path.startsWith("/sales/people/") ||
		path.startsWith("/sales/lead/")
	) {
		return "profile";
	}

	return "invalid";
}

/** Whether a Sales Navigator source can scrape `value` for people. */
export function isSalesNavigatorPeopleUrl(value: string): boolean {
	return SALES_NAVIGATOR_PEOPLE_URL_KINDS.includes(classifySalesNavigatorUrl(value));
}

const REFUSAL_BY_KIND: Readonly<Record<Exclude<SalesNavigatorUrlKind, "people_search" | "people_list">, string>> =
	{
		account_list:
			"This is a Sales Navigator account list. Paste a lead search or a lead list URL.",
		company_search:
			"This is a Sales Navigator company search. Paste a lead search or a lead list URL.",
		profile:
			"This is a LinkedIn profile. Paste a lead search or a lead list URL here, or add the person from the People tile, which takes profile URLs.",
		invalid:
			"This is not a Sales Navigator lead search or lead list URL. Paste a URL that starts with linkedin.com/sales/search/people or linkedin.com/sales/lists/people.",
	};

/**
 * The sentence shown beside the URL field when a source refuses it, naming what was pasted;
 * `null` for an accepted people search or list.
 */
export function salesNavigatorUrlRefusal(value: string): string | null {
	const kind = classifySalesNavigatorUrl(value);

	return kind === "people_search" || kind === "people_list" ? null : REFUSAL_BY_KIND[kind];
}
